<template>
  <el-dialog
    :title="$t('设置设备信息')"
    class="set-coords-dialog"
    :class="wrapClassName"
    :visible="true"
    :append-to-body="true"
    width="70%"
    top="5vh"
    cust
    :before-close="handleBeforeClose"
  >
    <div class="set-coords-dialog_content" v-loading="loading">
      <MapLibreGL ref="maplibre" @loaded="mapLoaded" />
      <div class="modify-position">
        <el-form inline size="mini" :model="formData" label-suffix=":">
          <el-form-item :label="$t('名称')">
            <el-input v-model="formData.name"></el-input>
          </el-form-item>
          <el-form-item :label="$t('设备位置')">
            <el-input v-model="formData.locationDesc"></el-input>
          </el-form-item>
          <el-form-item :label="$t('经度')">
            <el-input v-model="formData.longitude"></el-input>
          </el-form-item>
          <el-form-item :label="$t('纬度')">
            <el-input v-model="formData.latitude"></el-input>
          </el-form-item>
          <el-form-item class="ignore">
            <el-button type="primary" size="mini" @click="updateInfo">
              {{ $t('信息修改') }}
            </el-button>
          </el-form-item>
        </el-form>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import MapLibreGL from '@/components/maplibre';
import { stateMapMarker as smm } from '@/utils/dict';
import { isIntranet } from '@/utils/utils';
import { isEmpty } from '@/utils/is';
import { bmap2CGCS2000, toBmapPos2 } from '@/utils/bmapUtil';
import { updateDeviceInfo } from '@/api/devices';
import Type from '@/store/mutation-type';

export default {
  name: 'SetPositionMap',
  components: { MapLibreGL },
  props: {
    container: {
      type: [HTMLElement],
      default: document.body
    },
    wrapClassName: {
      type: [String, Array],
      default: ''
    },
    deviceInfo: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: true,
      mapApp: null,
      marker: null,
      labelPopup: null,
      formData: {
        sn: '',
        name: '',
        longitude: '',
        latitude: '',
        locationDesc: ''
      }
    };
  },
  computed: {
    ...mapGetters('socket', ['currentChooseDevice'])
  },
  methods: {
    ...mapMutations('socket', {
      modifyDeviceNameMuta: Type.MODIFY_DEVICE_INFO
    }),
    handleBeforeClose(done) {
      this.$destroy();
      done();
    },
    mapLoaded(mapApp) {
      this.loading = false;
      this.mapApp = mapApp;
      const map = mapApp.map;
      const device = isEmpty(this.deviceInfo) ? this.currentChooseDevice : this.deviceInfo;

      this.formData.sn = device.sn;
      this.formData.name = device.name;
      this.formData.longitude = device.longitude;
      this.formData.latitude = device.latitude;
      this.formData.locationDesc = device?.dynamicInfoMap?.location_desc || '';

      const coord = bmap2CGCS2000([device.longitude, device.latitude]);

      map.jumpTo({ center: coord, zoom: 11 });

      if (isIntranet()) {
        map.setMinZoom(3);
        map.setMaxZoom(13);
      }

      // Add marker
      const el = document.createElement('div');
      const img = document.createElement('img');
      img.src = smm[device.state] || smm['-1'];
      img.style.width = '32px';
      img.style.height = '32px';
      el.appendChild(img);

      this.marker = new maplibregl.Marker({ element: el }).setLngLat(coord).addTo(map);

      // Add label
      const labelEl = document.createElement('div');
      labelEl.className = 'device-label';
      labelEl.style.cssText =
        'color:#000;font-size:16px;border:2px solid #1E90FF;border-radius:8px;padding:2px 5px;background:#fff;white-space:nowrap;';
      labelEl.textContent = device.sn;

      this.labelPopup = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: [0, -50],
        className: 'device-label-popup'
      })
        .setLngLat(coord)
        .setDOMContent(labelEl)
        .addTo(map);

      // Right-click to reposition marker and update coordinates
      map.on('contextmenu', e => {
        const lngLat = [e.lngLat.lng, e.lngLat.lat];
        this.marker.setLngLat(lngLat);
        this.labelPopup.setLngLat(lngLat);
        this.formData.longitude = e.lngLat.lng;
        this.formData.latitude = e.lngLat.lat;
      });
    },
    updateInfo() {
      this.$confirm('确定修改设备相关信息吗', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const [lng, lat] = toBmapPos2({
          coordinate: [this.formData.longitude, this.formData.latitude]
        });
        const data = { ...this.formData, longitude: lng, latitude: lat };
        updateDeviceInfo(data).then(() => {
          this.$message({
            type: 'success',
            message: '修改成功',
            showClose: true
          });
          this.modifyDeviceNameMuta(this.formData);
        });
      });
    }
  },
  mounted() {
    /** @type {HTMLElement} */
    const container = this.container;

    container.appendChild(this.$el);
  },
  beforeDestroy() {
    if (this.marker) this.marker.remove();
    if (this.labelPopup) this.labelPopup.remove();

    /** @type {HTMLElement} */
    const el = this.$el;

    el.parentNode.removeChild(el);
  }
};
</script>

<style lang="scss">
.set-coords-dialog {
  .set-coords-dialog_content {
    height: 70vh;
    position: relative;
  }

  .modify-position {
    position: absolute;
    top: 10px;
    right: 10px;
    background-color: rgba(18, 30, 44, 0.6);
    padding: 5px;
    min-width: 800px;
    width: 80%;
    max-width: 1000px;
    z-index: 10;

    .el-form {
      display: flex;
      .el-form-item {
        display: flex;
        margin-bottom: 0;
        margin-right: 5px;
        .el-form-item__label {
          white-space: nowrap;
        }
      }
      .ignore {
        margin: 0;
      }
    }
  }

  .device-label-popup .maplibregl-popup-content {
    padding: 0;
    background: transparent;
    box-shadow: none;
  }

  .device-label-popup .maplibregl-popup-tip {
    display: none;
  }
}
</style>
